import api from './api';

export interface DueDatesData {
  thisWeek: number;
  nextWeek: number;
  overdue: number;
}

export interface StatusCountsData {
  [status: string]: number;
}

export interface InspectionTypeCountsData {
  [inspectionType: string]: number;
}

export interface TradeCountsData {
  [trade: string]: number;
}

export interface AverageTurnAroundTimeItem {
  inspectionType: string;
  inspectionTypeName: string;
  averageDays: number;
  count: number;
}

export const dashboardService = {
  /**
   * Get due dates overview (thisWeek, nextWeek, overdue)
   * @param workspaceId - Workspace ID
   * @param projectId - Project ID
   * @param trade - Optional trade filter
   */
  async getDueDates(
    workspaceId: string,
    projectId: string,
    trade?: string
  ): Promise<DueDatesData> {
    const params: any = {};
    if (trade) params.trade = trade;

    const response = await api.get(
      `/workspaces/${workspaceId}/${projectId}/deficiencies/due-dates`,
      { params }
    );
    return response.data;
  },

  /**
   * Get deficiency count by status
   * @param workspaceId - Workspace ID
   * @param projectId - Project ID
   * @param inspectionType - Optional inspection type filter
   * @param trade - Optional trade filter
   */
  async getDeficienciesByStatus(
    workspaceId: string,
    projectId: string,
    inspectionType?: string,
    trade?: string
  ): Promise<StatusCountsData> {
    const params: any = {};
    if (inspectionType) params.inspectionType = inspectionType;
    if (trade) params.trade = trade;

    const response = await api.get(
      `/workspaces/${workspaceId}/${projectId}/deficiencies/count-by-status`,
      { params }
    );
    return response.data;
  },

  /**
   * Get deficiency count by inspection type
   * @param workspaceId - Workspace ID
   * @param projectId - Project ID
   * @param status - Optional status filter
   * @param trade - Optional trade filter
   */
  async getDeficienciesByInspectionType(
    workspaceId: string,
    projectId: string,
    status?: string,
    trade?: string
  ): Promise<InspectionTypeCountsData> {
    const params: any = {};
    if (status) params.status = status;
    if (trade) params.trade = trade;

    const response = await api.get(
      `/workspaces/${workspaceId}/${projectId}/deficiencies/count-by-inspection-type`,
      { params }
    );
    return response.data;
  },

  /**
   * Get deficiency count by trade
   * @param workspaceId - Workspace ID
   * @param projectId - Project ID
   * @param inspectionType - Optional inspection type filter
   * @param trade - Optional trade filter
   */
  async getDeficienciesByTrade(
    workspaceId: string,
    projectId: string,
    inspectionType?: string,
    trade?: string
  ): Promise<TradeCountsData> {
    const params: any = {};
    if (inspectionType) params.inspectionType = inspectionType;
    if (trade) params.trade = trade;

    const response = await api.get(
      `/workspaces/${workspaceId}/${projectId}/deficiencies/count-by-trade`,
      { params }
    );
    return response.data;
  },

  /**
   * Get average turn around time by inspection type
   * @param workspaceId - Workspace ID
   * @param projectId - Project ID
   * @param trade - Optional trade filter
   */
  async getAverageTurnAroundTime(
    workspaceId: string,
    projectId: string,
    trade?: string
  ): Promise<AverageTurnAroundTimeItem[]> {
    const params: any = {};
    if (trade) params.trade = trade;

    const response = await api.get(
      `/workspaces/${workspaceId}/${projectId}/deficiencies/average-turn-around-time`,
      { params }
    );
    return response.data.data || [];
  },
};

export default dashboardService;
